import { useEffect, useState } from "react";
import { hitApi, dateFormatter } from "../../components/commonAction";
import AffiliatePageLayout from "./AffiliatePageLayout";

export default function Referrals({ pageTitle }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        hitApi({ url: "affiliate/referrals?limit=50", method: "GET", data: {}, alert: false }, (resp) => {
            setLoading(false);
            if (resp?.status) setRows(resp.data?.referrals || []);
        });
    }, []);

    const total = rows.length;
    const converted = rows.filter(r => r.convertedToUserId).length;
    const conversionRate = total > 0 ? Math.round((converted / total) * 100) : 0;

    return (
        <AffiliatePageLayout pageTitle={pageTitle} heading="Referrals">
            {loading ? (
                <ReferralsSkeleton />
            ) : (
                <>
                    {/* Stats summary */}
                    <div className="pxl-aff-stats">
                        <StatCard label="Total clicks"   value={total}                icon={<IconMouse />}  tone="default" />
                        <StatCard label="Converted"       value={converted}            icon={<IconCheck2 />} tone="success" />
                        <StatCard label="Conversion rate" value={`${conversionRate}%`} icon={<IconTrend />}  tone="accent"  />
                    </div>

                    <div className="pxl-aff-panel">
                        <div className="pxl-aff-panel-header">
                            <div>
                                <h3 className="pxl-aff-panel-title">Referral clicks</h3>
                                <div className="pxl-aff-panel-sub">Most recent first. Up to 50 shown.</div>
                            </div>
                            <span className="pxl-aff-panel-count">{rows.length}</span>
                        </div>

                        <div className="pxl-aff-table-wrap">
                            <table className="pxl-aff-table">
                                <thead>
                                    <tr>
                                        <th>When</th>
                                        <th>Landing page</th>
                                        <th>IP</th>
                                        <th>Converted?</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 ? (
                                        <tr><td colSpan="4" className="pxl-aff-table-state-cell">
                                            <EmptyState
                                                icon={<IconMouse />}
                                                title="No referral clicks yet"
                                                text="Clicks on your referral links will appear here in real time. Share your links from the Links tab to start tracking."
                                            />
                                        </td></tr>
                                    ) : rows.map(r => (
                                        <tr key={r._id}>
                                            <td><span className="pxl-aff-cell-strong">{dateFormatter(r.createdAt)}</span></td>
                                            <td><code className="pxl-aff-cell-code">{r.landingPath || "/"}</code></td>
                                            <td><span className="pxl-aff-cell-mute">{r.ipAddress || "—"}</span></td>
                                            <td>
                                                {r.convertedToUserId
                                                    ? <span className="pxl-aff-pill pxl-aff-pill--success"><IconDot /> Converted</span>
                                                    : <span className="pxl-aff-pill pxl-aff-pill--muted">Pending</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </AffiliatePageLayout>
    );
}

/* ── Skeleton ───────────────────────────────────────────────────────────────── */
function ReferralsSkeleton() {
    return (
        <>
            {/* 3-card stat row */}
            <div className="pxl-aff-stats" style={{ marginBottom: 20 }}>
                {[0, 1, 2].map(i => (
                    <div className="pxl-sk-card" key={i}>
                        <span className="pxl-sk" style={{ width: '55%', height: 10, display: 'block' }} />
                        <span className="pxl-sk" style={{ width: '45%', height: 26, display: 'block', marginTop: 12 }} />
                    </div>
                ))}
            </div>

            {/* Table panel */}
            <div className="pxl-aff-panel">
                <div className="pxl-aff-panel-header">
                    <span className="pxl-sk" style={{ width: 120, height: 13, display: 'inline-block' }} />
                </div>
                <div className="pxl-aff-table-wrap">
                    <table className="pxl-aff-table">
                        <thead>
                            <tr>
                                <th>When</th>
                                <th>Landing page</th>
                                <th>IP</th>
                                <th>Converted?</th>
                            </tr>
                        </thead>
                        <tbody>
                            {[0, 1, 2, 3, 4].map(i => (
                                <tr key={i}>
                                    <td><span className="pxl-sk" style={{ width: '70%', height: 13, display: 'block' }} /></td>
                                    <td><span className="pxl-sk" style={{ width: '55%', height: 13, display: 'block' }} /></td>
                                    <td><span className="pxl-sk" style={{ width: '40%', height: 13, display: 'block' }} /></td>
                                    <td><span className="pxl-sk" style={{ width: 60, height: 22, borderRadius: 11, display: 'block' }} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

/* ── Sub-components ────────────────────────────────────────────────────────── */
function StatCard({ label, value, icon, tone = "default" }) {
    return (
        <div className={`pxl-aff-stat pxl-aff-stat--${tone}`}>
            <div className="pxl-aff-stat-head">
                <span className="pxl-aff-stat-label">{label}</span>
                <span className="pxl-aff-stat-icon">{icon}</span>
            </div>
            <div className="pxl-aff-stat-value">{value}</div>
        </div>
    );
}
function EmptyState({ icon, title, text }) {
    return (
        <div className="pxl-aff-empty">
            <div className="pxl-aff-empty-icon">{icon}</div>
            <div className="pxl-aff-empty-title">{title}</div>
            <p className="pxl-aff-empty-text">{text}</p>
        </div>
    );
}

/* ── Icons ─────────────────────────────────────────────────────────────────── */
function IconMouse()  { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6a3 3 0 0 1 6 0v6a3 3 0 0 1-6 0z"/><path d="M12 1v3"/><path d="M12 17v6"/></svg>; }
function IconCheck2() { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>; }
function IconTrend()  { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>; }
function IconDot()    { return <svg width="6" height="6" viewBox="0 0 6 6" fill="currentColor"><circle cx="3" cy="3" r="3"/></svg>; }
